import { Project, User } from '@/app/api';
import Button, { ButtonHTMLTag, ButtonSize } from '@/app/components/Button/Button';
import Dialog from '@/app/components/Dialog/Dialog';
import InstagramIcon from '@/app/components/InstagramIcon/InstagramIcon';
import { LinkIcon } from '@/app/components/LinkIcon/LinkIcon';
import { UtmCampaign, UtmMedium, UtmSource } from '@/app/enums';
import useAnalytics from '@/app/hooks/useAnalytics';
import { ShareMethod } from '@/app/hooks/useAnalytics/events/project-detail/shared-event';
import { useToast } from '@/app/hooks/useToast';
import { getBaseUrlForSharing, getShareLink } from '@/app/utils/social';
import * as Sentry from '@sentry/nextjs';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ShareInstagramDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  subProjectId?: string;
  user?: User;
};

const share = async (base64: string) => {
  const blob = await (await fetch(base64)).blob();
  const file = new File([blob], 'story.png', { type: blob.type });

  window.navigator
    ?.share({
      title: undefined,
      text: undefined,
      files: [file],
    })
    .catch((error) => {
      Sentry.captureException(error);
      console.error('oops, something went wrong!', error);
    });
};

const imageUrlToBase64 = async (url: string): Promise<string | ArrayBuffer | null> => {
  const response = await fetch(url);

  if (!response.ok) {
    return null;
  }
  const blob = await response.blob();
  return new Promise((onSuccess, onError) => {
    const reader = new FileReader();
    reader.onload = function () {
      onSuccess(this.result);
    };
    reader.readAsDataURL(blob);
  });
};

export const ShareInstagramDialog = ({ isOpen, onClose, project, user, subProjectId }: ShareInstagramDialogProps) => {
  const [displaySuccessMessage, setDisplaySuccessMessage] = useState(false);
  const {
    projectDetail: { sharedEvent },
  } = useAnalytics();
  const { t } = useTranslation();
  const { showErrorToast } = useToast();

  const shareUrl = useMemo(() => {
    return getShareLink(getBaseUrlForSharing(project.slug), {
      utmSource: UtmSource.INSTAGRAM,
      utmMedium: UtmMedium.SOCIAL_SHARE,
      utmCampaign: UtmCampaign.SHARE_BUTTON,
      utmContent: user?.id ?? `anonymous-${Math.random().toString(36).substring(7)}`,
    });
  }, [project.slug]);

  const handleShareOnInstagram = useCallback(async () => {
    try {
      const base64 = await imageUrlToBase64(
        `/next-api/story-image?projectId=${project.id}${subProjectId ? `&subProjectId=${subProjectId}` : ''}`,
      );

      if (!base64 || typeof base64 !== 'string') {
        return showErrorToast(t('components.InstagramShare.errorWhileSharingImage'));
      }
      share(base64);
      onClose();
    } catch (error: unknown) {
      Sentry.captureException(error);
      console.log(error);
      showErrorToast(t('components.InstagramShare.errorWhileSharingImage'));
    }
  }, [subProjectId, project.id]);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    setDisplaySuccessMessage(true);
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div className="px-[16px]">
        <h1 className="leading-[32px]">{t('components.InstagramShare.title')}</h1>
      </div>
      <div className="flex justify-center p-[16px]">
        <img alt="how to share animation" width={222} src={'/public/images/ig-share-animation.gif'} />
      </div>
      <div className="h-[48px] flex items-end justify-center">
        <div
          className={classNames(
            'flex items-center justify-center gap-[8px] transition-all transition-300  overflow-hidden',
            displaySuccessMessage ? 'h-[48px] opacity-100' : 'h-[0px] opacity-0',
          )}
        >
          <LinkIcon className="text-success-mid" />
          <p className="text-center text-success-mid">{t('components.InstagramShare.copyAddressSuccess')}</p>
        </div>
      </div>
      {displaySuccessMessage ? (
        <Button
          className="w-full p-[16px]"
          size={ButtonSize.LARGE}
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.BUTTON,
            onClick: () => {
              sharedEvent(project, ShareMethod.INSTAGRAM);
              handleShareOnInstagram();
            },
          }}
        >
          <div className="flex items-center justify-center gap-[8px]">
            <InstagramIcon />
            <span>{t('components.InstagramShare.createInstagramStory')}</span>
          </div>
        </Button>
      ) : (
        <Button
          className="w-full p-[16px]"
          size={ButtonSize.LARGE}
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: handleCopyToClipboard }}
        >
          <div className="flex items-center justify-center gap-[8px]">
            <LinkIcon />
            <span>{t('components.InstagramShare.copyProjectLink')}</span>
          </div>
        </Button>
      )}
    </Dialog>
  );
};
